import jwtDecode from 'jwt-decode';
import axios from 'axios';

import config from '../../config/config';

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.get['Accept'] = 'application/json';
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error: unknown) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let message;

        if (axios.isCancel(error)) {
            if (process.env.NODE_ENV === 'development') {
                console.log('request canceled in cleanup');
            }
            return Promise.reject(error);
        }

        if (!axios.isAxiosError(error)) {
            return Promise.reject(error);
        }

        if (error && error.response && error.response.status === 404) {
            // window.location.href = '/not-found';
        } else if (error && error.response && error.response.status === 403) {
            // window.location.href = '/access-denied';
        } else if (error && error.response && error.response.status === 422 && error.response.data) {
            // erreur de validation
            return Promise.reject(error.response.data);
        } else {
            if (!error.response) {
                // network error
                message = 'Network Error';
            } else {
                switch (error.response.status) {
                    case 401:
                        message = 'Invalid credentials';
                        break;
                    default: {
                        message =
                            error.response && error.response.data ? error.response.data : error;
                    }
                }
            }
            return Promise.reject(message);
        }
    }
);

const AUTH_SESSION_KEY = 'Token';

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common['Authorization'] = 'Token ' + token;
    else delete axios.defaults.headers.common['Authorization'];
};

const getUserFromCookie = () => {
    const user = localStorage.getItem(AUTH_SESSION_KEY);
    return user ? (typeof user == 'object' || 'string' ? user : JSON.parse(user)) : null;
};
class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params?: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
            response = axios.get(`${axios.defaults.baseURL}${url}?${queryString}`, params);
        } else {
            response = axios.get(`${axios.defaults.baseURL}${url}`);
        }
        return response;
    };

    getFile = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
            response = axios.get(`${axios.defaults.baseURL}${url}?${queryString}`, { responseType: 'blob' });
        } else {
            response = axios.get(`${axios.defaults.baseURL}${url}`, { responseType: 'blob' });
        }
        return response;
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = '';
        if (params) {
            queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
        }

        for (const url of urls) {
            reqs.push(axios.get(`${axios.defaults.baseURL}${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any) => {
        return axios.post(`${axios.defaults.baseURL}${url}`, data);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(`${axios.defaults.baseURL}${url}`, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(`${axios.defaults.baseURL}${url}`, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(`${axios.defaults.baseURL}${url}`);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        // @ts-ignore
        return axios.post(`${axios.defaults.baseURL}${url}`, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        // @ts-ignore
        return axios.patch(`${axios.defaults.baseURL}${url}`, formData, config);
    };

    isUserAuthenticated = () => {
        const user = this.getLoggedInUser();

        if (!user) {
            return false;
        }
        const decoded: any = jwtDecode(user.token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            console.warn('access token expired');
            return false;
        } else {
            return true;
        }
    };

    setLoggedInUser = (session: any) => {
        if (session) localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        else {
            localStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromCookie();
    };

    setUserInSession = (modifiedUser: any) => {
        let userInfo = localStorage.getItem(AUTH_SESSION_KEY);
        if (userInfo) {
            const { token, user } = JSON.parse(userInfo);
            this.setLoggedInUser({ token, ...user, ...modifiedUser });
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
    const { tokenProperty } = user;
    const token = user;
    if (token || tokenProperty) {
        setAuthorization(token || tokenProperty);
    }
}

export { APICore, setAuthorization };
